/**
 * Centralized storage keys configuration
 * All keys use 'tonesim:' prefix to avoid conflicts with other apps
 * 
 * IMPORTANT: Never change these keys in production without migration!
 */

export const STORAGE_PREFIX = 'tonesim:';

/**
 * Create storage key with prefix
 */
function key(name: string): string {
  return `${STORAGE_PREFIX}${name}`;
}

/**
 * Ensure a key has the app prefix to avoid collisions.
 * If the key is already prefixed, returns it unchanged.
 */
export function ensurePrefixedStorageKey(rawKey: string): string {
  if (!rawKey) return rawKey;
  return rawKey.startsWith(STORAGE_PREFIX) ? rawKey : `${STORAGE_PREFIX}${rawKey}`;
}

/**
 * Storage keys used throughout the application
 */
export const STORAGE_KEYS = {
  // Authentication
  AUTH_ACCESS_TOKEN: key('auth:accessToken'),
  AUTH_REFRESH_TOKEN: key('auth:refreshToken'),
  
  // Telecom
  TELECOM_TOKEN: key('telecom:token'),
  
  // Active eSIM
  ACTIVE_ESIM_ID: key('activeEsimId'),
  LAST_USED_ESIM_BY_WALLET: key('lastUsedEsimByWallet'),
  
  // Developer mode
  DEV_MODE: key('dev-mode'),
  
  // Debug logging
  DEBUG_LOGS: key('debug-logs'),

  // Custodial mint order flow
  ACTIVE_MINT_ORDER: key('telemint:activeMintOrder'),
  ACTIVE_MINT_HANDOFF: key('telemint:activeMintHandoff'),
  ACTIVE_GIFT_MINT_SUBMISSION: key('telemint:activeGiftMintSubmission'),
} as const;

/**
 * Check if a key belongs to this app (has correct prefix)
 */
export function isAppStorageKey(key: string): boolean {
  return key.startsWith(STORAGE_PREFIX);
}

/**
 * Get all app storage keys from localStorage
 * Useful for debugging and cleanup
 */
export function getAllAppStorageKeys(): string[] {
  if (typeof window === 'undefined' || !window.localStorage) {
    return [];
  }
  
  const keys: string[] = [];
  for (let i = 0; i < window.localStorage.length; i++) {
    const key = window.localStorage.key(i);
    if (key && isAppStorageKey(key)) {
      keys.push(key);
    }
  }
  return keys;
}

/**
 * Clear all app storage (for logout/reset)
 * WARNING: This removes ALL tonesim:* keys!
 */
export function clearAllAppStorage(): void {
  if (typeof window === 'undefined' || !window.localStorage) {
    return;
  }
  
  const keys = getAllAppStorageKeys();
  keys.forEach(key => {
    try {
      window.localStorage.removeItem(key);
    } catch (error) {
      // Silently fail - logger not available here to avoid circular dependency
    }
  });
}
